import Vue from 'vue'
import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/functions'
import { TEST_STATES } from '@/constants/tests'
import { functions } from '@/firebase'
import { debounce } from '@/utils/debounce'

const db = firebase.database()

export default {
  namespaced: true,
  state: {
    filteredUsers: [],
    tests: {},
    testsRef: null,
    enterpriseAccounts: {},
    enterpriseAccountRef: null,
    suspendedEmailDomains: []
  },
  getters: {
    tests (state) {
      return state.tests
    },
    test (state) {
      return (id) => {
        return state.tests[id]
      }
    },
    enterpriseAccounts (state) {
      return state.enterpriseAccounts
    },
    enterpriseAccount (state) {
      return (id) => {
        return state.enterpriseAccounts[id]
      }
    },
    filteredUsers (state) {
      return state.filteredUsers
    },
    suspendedEmailDomains (state) {
      const domains = []
      if (state?.suspendedEmailDomains) {
        for (const [key, value] of Object.entries(state?.suspendedEmailDomains)) {
          domains.push({
            uid: key,
            ...value
          })
        }
      }
      return domains
    }
  },
  mutations: {
    toggleAdminForUser (state, payload) {
      if (state.filteredUsers && state.filteredUsers[payload.userId]) {
        Vue.set(state.filteredUsers[payload.userId], 'isAdmin', !state.filteredUsers[payload.userId].isAdmin)
      }
    },
    toggleSuspiciousStatusForUser (state, payload) {
      if (state.filteredUsers && state.filteredUsers[payload.userId]) {
        Vue.set(state.filteredUsers[payload.userId], 'isSuspiciousAccount', !state.filteredUsers[payload.userId].isSuspiciousAccount)
      }
    },
    setTests (state, payload) {
      if (payload) state.tests = payload
      else state.tests = {}
    },
    setTestsRef (state, payload) {
      state.testsRef = payload
    },
    setFilteredUsers(state, { filteredUsers }) {
      state.filteredUsers = filteredUsers
    },
    setEnterpriseAccounts (state, { accounts }) {
      state.enterpriseAccounts = accounts
    },
    setEnterpriseAccountRef (state, ref) {
      state.enterpriseAccountRef = ref
    },
    setEnterpriseAccount (state, { accountId, account }) {
      state.enterpriseAccounts[accountId] = account
    },
    setSuspendedEmailsDomains (state, suspendedEmailDomains) {
      state.suspendedEmailDomains = suspendedEmailDomains
    }
  },
  actions: {
    // Manage User Page
    async updateUserMFAStatus ({}, { status, uid }) {
      const userRef = firebase.database().ref(`/users/${uid}`)
      await userRef.update({
        mfaState: status
      })
      return 'updated'
    },
    async cancelUsersMFA ({}, uid) {
      try {
        await functions.httpsCallable('mfa-cancelUsersMFA')({ uid: uid })
        return 'canceled'
      } catch (err) {
        throw new Error(err)
      }
    },
    async shareUsersProject ({}, { testId, enterprise }) {
      const testRef = firebase.database().ref(`/concept_tests/${testId}`)
      await testRef.update({
        enterprise: enterprise
      })
    },
    async unshareUsersProject ({}, { testId }) {
      const testRef = firebase.database().ref(`/concept_tests/${testId}`)
      await testRef.update({
        enterprise: null
      })
    },
    async shareAllUsersProjects ({}, { userId, enterprise }) {
      await functions.httpsCallable('admin-shareEnterpiseUserProjects')({ userUid: userId, enterprise })
      return 'updated'
    },
    async handoverAllUsersProjects ({}, { sourceUid, destinationUid }) {
      await functions.httpsCallable('admin-handoverAllUsersProjects')({ sourceUid, destinationUid })
      return 'updated'
    },
    // Manage User Page ends
    async runStatsCheck () {
      await functions.httpsCallable('updateUserStatsOnAllUsers')()
    },

    async fetchUsersByEmailAndDomain ({ commit }, { queryString }) {
      const isQueryAFullEmail = queryString.split('@').length === 2
      let databaseUsersRef = ''

      // If admin submitted a full email, we search for exact much,
      // otherwise we assuming that admin is filtering by emailDomain
      if (isQueryAFullEmail) {
        databaseUsersRef = db.ref('/users').orderByChild('email').equalTo(queryString)
      } else {
        databaseUsersRef = db.ref('/users').orderByChild('emailDomain').equalTo(queryString)
      }

      databaseUsersRef.on('value', debounce((snapshot) => {
        const users = snapshot.val()
        commit('setFilteredUsers', { filteredUsers: users })
      }, 200))
    },

    async toggleAdminForUser ({ state, commit }, { userId }) {
      commit('toggleAdminForUser', { userId })
      await firebase
        .database()
        .ref(`users/${userId}`)
        .update({
          isAdmin: state.filteredUsers[userId].isAdmin
        })
    },
    async toggleSuspiciousnessStatus ({ state, commit}, { userId }) {
      commit('toggleSuspiciousStatusForUser', { userId })

      await firebase
        .database()
        .ref(`users/${userId}`)
        .update({
          isSuspiciousAccount: state.filteredUsers[userId].isSuspiciousAccount
        })
    },

    async suspendEmailDomain ({ dispatch }, { emailDomain }) {
      try {
        await firebase
          .database()
          .ref('suspended_email_domains')
          .push({
            emailDomain,
            updated: Date.now(),
            created: Date.now(),
            isSuspended: true
          })
          
          await dispatch('loadSuspendedEmailDomains')
        } catch (err) {
          throw new Error({
            message: err?.message || 'Failed to suspend an email domain.'
          })
        }
    },

    async toggleEmailDomainSuspension ({ dispatch }, { emailRecordId, isSuspended }) {
      try {
        await firebase
          .database()
          .ref(`suspended_email_domains/${emailRecordId}`)
          .update({
            updated: Date.now(),
            isSuspended: !isSuspended
          })

        return dispatch('loadSuspendedEmailDomains')
      } catch (err) {
        throw new Error({
          message: err?.message || 'Failed to toggle suspended email status.'
        })
      }
    },

    async loadSuspendedEmailDomains ({ commit }) {
      try {
        const emailDomains = (await firebase
          .database()
          .ref('suspended_email_domains')
          .once('value')).val()
        commit('setSuspendedEmailsDomains', emailDomains)
      } catch (err) {
        throw new Error({
         message: err?.message || 'Failed to fetch suspended email domains.'
        })
      } 
    },

    loadPublishedTests ({ state, commit }) {
      if (state.testsRef) {
        // Make sure we only register one value listener at a time
        state.testsRef.off()
      }

      const testsRef = firebase
        .database()
        .ref('concept_tests')
      commit('setTestsRef', testsRef)

      testsRef.on('value', snapshot => {
        const tests = snapshot.val()
        const filteredTests = {}
        Object.keys(tests).forEach(key => {
          if (tests[key].state !== TEST_STATES.EDITING) {
            filteredTests[key] = tests[key]
          }
        })
        commit('setTests', filteredTests)
      })
    },
    unloadPublishedTests ({ state }) {
      if (state.testsRef) {
        // Stop listening to updates
        state.testsRef.off()
      }
    },
    async getUserAuthData (context, userId) {
      const result = await functions.httpsCallable('getUserAuthData')({ userId })
      return result.data
    },
    async setUserOnTrial (context, payload) {
      return functions.httpsCallable('billing-setUserOnTrial')(payload)
    },
    async endUserTrial (context, payload) {
      return functions.httpsCallable('billing-endUserTrial')(payload)
    },
    async loadEnterpriseAccounts ({ state, commit }) {
      if (state.enterpriseAccountRef) {
        // Make sure we only register one value listener at a time
        state.enterpriseAccountRef.off()
      }

      const accountRef = firebase
        .database()
        .ref('enterprise')
      commit('setEnterpriseAccountRef', accountRef)

      accountRef.on('value', snapshot => {
        const accounts = snapshot.val()
        Object.keys(accounts).forEach(k => { accounts[k].uid = k })
        commit('setEnterpriseAccounts', { accounts })
      })
    },
    async createEnterpriseAccount ({}, { account }) {
      const ref = await firebase
        .database()
        .ref('enterprise')
        .push(account)
      return ref.key
    },
    async saveEnterpriseAccount ({}, { accountId, account }) {
      await firebase
        .database()
        .ref('enterprise')
        .child(accountId)
        .update(account)
    },
    async deleteEnterpriseAccount (context, { accountId }) {
      await functions.httpsCallable('removeEnterpriseAccount')({ accountId })
    },
    async saveEnterpriseDocument (context, { accountId, document }) {
      await firebase
        .database()
        .ref('enterprise')
        .child(accountId)
        .child('documents')
        .push(document)
    },
    async removeEnterpriseDocument (context, { accountId, documentId, document }) {
      await firebase
        .database()
        .ref('enterprise')
        .child(accountId)
        .child('documents')
        .child(documentId)
        .remove()
      await firebase
        .storage()
        .ref(document.storageRef)
        .delete()
    },
    async getHighLevelStats () {
      return await functions.httpsCallable('getHighLevelStats')()
    }
  }
}
